import React, { useState } from "react";
import { Form, Input, InputNumber, Button, Switch, Space } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import { CreateSquareIntegrationRequest } from "./types";

type SquareIntegrationFormProps = {
  restaurantId: string;
  onSuccess: () => void;
};

export const SquareIntegrationForm: React.FC<SquareIntegrationFormProps> = ({
  restaurantId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { createSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = (values: {
    access_token: string;
    location_id: string;
    tax_percentage?: string;
    use_for_online_payment?: boolean;
  }) => {
    setLoading(true);

    const request: CreateSquareIntegrationRequest = {
      access_token: values.access_token,
      location_id: values.location_id,
      tax_percentage: values.tax_percentage || undefined,
      use_for_online_payment: values.use_for_online_payment || false,
    };

    fetchT<{ id: string }>(
      "POST",
      `${API_URL}/v1/r/${restaurantId}/square/integrations`,
      request,
      () => {
        createSuccess(t("square_integration"));
        form.resetFields();
        onSuccess();
        setLoading(false);
      },
      (err) => {
        notifyError(t("failed_to_create_integration"), err);
        setLoading(false);
      }
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ use_for_online_payment: false }}
    >
      <Form.Item
        name="access_token"
        label={t("access_token")}
        rules={[{ required: true, message: t("access_token_required") }]}
      >
        <Input.Password placeholder={t("enter_access_token")} />
      </Form.Item>

      <Form.Item
        name="location_id"
        label={t("location_id")}
        rules={[{ required: true, message: t("location_id_required") }]}
      >
        <Input placeholder={t("enter_location_id")} />
      </Form.Item>

      <Form.Item
        name="tax_percentage"
        label={t("tax_percentage")}
        tooltip={t("tax_percentage_tooltip")}
        dependencies={["use_for_online_payment"]}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const useForOnline = getFieldValue("use_for_online_payment");
              const hasValue = value !== undefined && value !== null && value !== "";

              if (useForOnline && !hasValue) {
                return Promise.reject(new Error(t("tax_percentage_required")));
              }

              if (!hasValue) {
                return Promise.resolve();
              }

              const str = String(value);
              const decimalRegex = /^\d+(\.\d{1,5})?$/;
              if (!decimalRegex.test(str)) {
                return Promise.reject(new Error(t("tax_percentage_invalid_format")));
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          stringMode
          precision={5}
          min={0}
          placeholder={t("enter_tax_percentage")}
        />
      </Form.Item>

      <Form.Item
        name="use_for_online_payment"
        label={t("use_for_online_payment")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("create_integration")}
          </Button>
          <Button onClick={() => form.resetFields()}>{t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
